import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import PortalLayout from "../components/PortalLayout";
import AdminSubNav from "../components/AdminSubNav";
import { useAdminAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Users, Search, TrendingUp } from "lucide-react";

const fmt = (iso) =>
    iso ? new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—";

function GrowthChart() {
    const [growth, setGrowth] = useState(null);

    useEffect(() => {
        let cancelled = false;
        api.get("/admin/members/growth")
            .then((res) => { if (!cancelled) setGrowth(res.data); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, []);

    if (!growth) return null;
    const max = Math.max(1, ...growth.weeks.map((w) => w.joins));
    const weekLabel = (iso) => new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
    return (
        <div className="border border-white/10 bg-white/[0.02] rounded p-5 mb-8" data-testid="members-growth-chart">
            <div className="flex flex-wrap items-center gap-2 mb-4">
                <TrendingUp className="w-4 h-4 text-[#cfa052]" aria-hidden="true" />
                <h2 className="font-serif-display text-lg text-[#e8e4db] tracking-tight">Joins per week</h2>
                <span className="ml-auto font-label text-[9px] tracking-[0.2em] uppercase text-[#6e6a62]" data-testid="members-growth-pace">
                    {growth.remaining > 0
                        ? `${growth.total} members · ${growth.remaining} to go until ${growth.milestone}`
                        : `${growth.total} members · milestone of ${growth.milestone} reached`}
                </span>
            </div>
            <div className="flex items-end gap-1.5 h-28" data-testid="members-growth-bars">
                {growth.weeks.map((w) => (
                    <div key={w.weekStart} className="flex-1 flex flex-col items-center gap-1 min-w-0 group" data-testid={`growth-week-${w.weekStart}`}>
                        <span className="font-numerals text-[10px] text-[#9a958a] group-hover:text-[#cfa052] transition-colors">{w.joins || ""}</span>
                        <div
                            className="w-full max-w-[26px] bg-[#cfa052]/80 group-hover:bg-[#e0b569] rounded-t-sm transition-colors"
                            style={{ height: `${Math.max(w.joins > 0 ? 6 : 2, Math.round((w.joins / max) * 72))}px` }}
                            title={`Week of ${weekLabel(w.weekStart)} — ${w.joins} join${w.joins === 1 ? "" : "s"} (total ${w.cumulative})`}
                        />
                        <span className="font-label text-[7px] tracking-[0.05em] uppercase text-[#6e6a62] truncate w-full text-center">{weekLabel(w.weekStart)}</span>
                    </div>
                ))}
            </div>

            {growth.coverage?.length > 0 && (() => {
                const maxMembers = Math.max(1, ...growth.coverage.map((c) => c.members));
                return (
                    <div className="mt-8 pt-5 border-t border-white/5" data-testid="members-coverage-chart">
                        <div className="flex flex-wrap items-center gap-2 mb-3">
                            <h3 className="font-serif-display text-base text-[#e8e4db] tracking-tight">Angel coverage</h3>
                            <span className="ml-auto font-label text-[9px] tracking-[0.2em] uppercase text-[#6e6a62]" data-testid="members-coverage-caption">
                                {growth.coveredCount} of {growth.coverage.length} chosen · {growth.coverage.length - growth.coveredCount} waiting for their first member
                            </span>
                        </div>
                        <div className="grid grid-cols-10 sm:grid-cols-15 gap-1.5" style={{ gridTemplateColumns: "repeat(15, minmax(0, 1fr))" }}>
                            {growth.coverage.map((c) => (
                                <div
                                    key={c.number}
                                    title={c.members > 0
                                        ? `Angel ${c.number} — ${c.members} member${c.members === 1 ? "" : "s"}`
                                        : `Angel ${c.number} — waiting for its first member`}
                                    data-testid={`coverage-cell-${c.number}`}
                                    className={`aspect-square rounded-sm flex items-center justify-center font-numerals text-[9px] transition-colors cursor-default ${
                                        c.members > 0 ? "text-[#0c0b0a]" : "text-[#6e6a62] border border-white/10 bg-white/[0.02]"
                                    }`}
                                    style={c.members > 0 ? { backgroundColor: `rgba(207, 160, 82, ${0.35 + 0.65 * (c.members / maxMembers)})` } : undefined}
                                >
                                    {c.number.replace(/^0+/, "")}
                                </div>
                            ))}
                        </div>
                    </div>
                );
            })()}
        </div>
    );
}

function StatPill({ label, value, testid }) {
    return (
        <div className="border border-white/10 bg-white/[0.02] rounded p-5" data-testid={testid}>
            <div className="font-numerals text-3xl text-[#cfa052]">{value}</div>
            <div className="mt-1 font-label text-[9px] tracking-[0.25em] uppercase text-[#6e6a62]">{label}</div>
        </div>
    );
}

export default function AdminMembers() {
    const { admin } = useAdminAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await api.get("/admin/members");
                if (!cancelled) setData(res.data);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const filtered = useMemo(() => {
        if (!data) return [];
        const q = query.trim().toLowerCase();
        if (!q) return data.members;
        return data.members.filter((m) =>
            (m.fullName || "").toLowerCase().includes(q)
            || (m.email || "").toLowerCase().includes(q)
            || (m.angelNumber || "").includes(q)
        );
    }, [data, query]);

    if (admin === null) return null;
    if (!admin) return <Navigate to="/login" replace />;

    return (
        <PortalLayout>
            <section className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16 pt-16 pb-32" data-testid="admin-members-page">
                <div className="mb-10">
                    <span className="font-label text-[11px] text-[#cfa052]">Admin · members</span>
                    <h1 className="mt-4 font-serif-display text-5xl md:text-6xl tracking-tighter text-[#e8e4db]">
                        The <span className="italic">squad</span>
                    </h1>
                    <p className="mt-4 font-poetic text-[#9a958a] max-w-2xl">
                        Everyone who has arrived from the Squad app — who they are, which Angel they chose, and when they last visited.
                    </p>
                </div>

                <AdminSubNav />

                {loading ? (
                    <p className="font-poetic text-[#6e6a62]">Gathering the squad…</p>
                ) : !data?.members?.length ? (
                    <div className="border border-white/10 bg-white/[0.02] rounded p-10 text-center" data-testid="admin-members-empty">
                        <Users className="w-6 h-6 text-[#cfa052] mx-auto" aria-hidden="true" />
                        <p className="mt-4 font-poetic text-[#9a958a]">
                            No members yet — the roster fills as people open the portal from the Squad app.
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                            <StatPill label="Members" value={data.total} testid="admin-members-total" />
                            <StatPill label="Picked their Angel" value={data.withSelection} testid="admin-members-with-selection" />
                            <StatPill label="Active last 7 days" value={data.activeLast7Days} testid="admin-members-active" />
                        </div>

                        <GrowthChart />

                        <div className="relative max-w-sm mb-6">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6e6a62]" aria-hidden="true" />
                            <input
                                type="search"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Search by name, email or angel number"
                                aria-label="Search members"
                                data-testid="admin-members-search"
                                className="w-full bg-[#151311] border border-white/10 focus:border-[#cfa052]/60 focus:outline-none text-[#e8e4db] placeholder:text-[#6e6a62] text-sm font-poetic rounded-sm pl-10 pr-4 py-2.5 transition-colors"
                            />
                        </div>

                        <div className="overflow-x-auto border border-white/10 rounded" data-testid="admin-members-table">
                            <table className="w-full text-left">
                                <thead>
                                    <tr className="border-b border-white/10 bg-white/[0.02]">
                                        {["Member", "Email", "Joined", "Angel", "Last visit", "Visits"].map((h) => (
                                            <th key={h} className="px-4 py-3 font-label text-[9px] tracking-[0.25em] uppercase text-[#6e6a62]">{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.map((m) => (
                                        <tr key={m.memberId} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors" data-testid={`admin-member-row-${m.memberId}`}>
                                            <td className="px-4 py-3 font-poetic text-sm text-[#e8e4db]">{m.fullName || "—"}</td>
                                            <td className="px-4 py-3 font-poetic text-sm text-[#9a958a]">{m.email || <span className="text-[#6e6a62] italic">no email</span>}</td>
                                            <td className="px-4 py-3 font-poetic text-sm text-[#9a958a]">{fmt(m.joinedAt)}</td>
                                            <td className="px-4 py-3">
                                                {m.angelNumber ? (
                                                    <span className="font-numerals text-[#cfa052]">№ {m.angelNumber}</span>
                                                ) : (
                                                    <span className="font-poetic text-sm text-[#6e6a62] italic">not yet</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 font-poetic text-sm text-[#9a958a]">{fmt(m.lastSeenAt)}</td>
                                            <td className="px-4 py-3 font-numerals text-sm text-[#9a958a]">{m.visits}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {!filtered.length && (
                                <p className="p-6 font-poetic text-sm text-[#6e6a62]" data-testid="admin-members-no-match">
                                    No members match “{query}”.
                                </p>
                            )}
                        </div>
                    </>
                )}
            </section>
        </PortalLayout>
    );
}
